/**
 * General enums
 */
/**
 * Virtual Machine types supported by the platform
 */
export var VMType;
(function (VMType) {
    VMType["EVM"] = "evm";
    VMType["SVM"] = "svm";
    VMType["OFFCHAIN"] = "offchain";
})(VMType || (VMType = {}));
/**
 * Platform environment (mainnet vs testnet)
 */
export var PlatformEnvironment;
(function (PlatformEnvironment) {
    PlatformEnvironment["TESTNET"] = "testnet";
    PlatformEnvironment["MAINNET"] = "mainnet";
    PlatformEnvironment["LOCAL"] = "local";
    PlatformEnvironment["UNDEFINED"] = "undefined";
})(PlatformEnvironment || (PlatformEnvironment = {}));
/**
 * Account types
 */
export var AccountType;
(function (AccountType) {
    AccountType["EOA"] = "eoa";
    AccountType["SMART_CONTRACT"] = "smart_contract";
})(AccountType || (AccountType = {}));
export var OperationType;
(function (OperationType) {
    OperationType["APPROVE_ERC20_TOKEN"] = "approve_erc20_token";
    OperationType["CANCEL_OPERATION"] = "cancel_operation";
    OperationType["CANCEL_TRANSACTION"] = "cancel_transaction";
    OperationType["FINAL_TRANSACTION"] = "final_transaction";
    OperationType["GAS_ABSTRACTION"] = "gas_abstraction";
    OperationType["NATIVE_TOKEN_TRANSFER"] = "native_token_transfer";
    OperationType["SUBMIT_INTENT"] = "submit_intent";
    OperationType["POLYMARKET_ORDER"] = "polymarket_order";
    OperationType["POLYMARKET_L2_SETUP"] = "polymarket_l2_setup";
    OperationType["HYPERLIQUID_SETUP"] = "hyperliquid_setup";
})(OperationType || (OperationType = {}));
export var Platform;
(function (Platform) {
    Platform["ETHEREUM"] = "ethereum";
    Platform["POLYGON"] = "polygon";
    Platform["BINANCE"] = "binance";
    Platform["ARBITRUM"] = "arbitrum";
    Platform["BASE"] = "base";
    Platform["AVALANCHE"] = "avalanche";
    Platform["ARBITRUM_NOVA"] = "arbitrum_nova";
    Platform["OPTIMISM"] = "optimism";
    Platform["EVMOS"] = "evmos";
    Platform["MOONBEAM"] = "moonbeam";
    Platform["SOLANA"] = "solana";
    Platform["SONIC"] = "sonic";
    Platform["MANTLE"] = "mantle";
    Platform["HYPERLIQUID_PLATFORM"] = "hyperliquid_platform";
    Platform["HYPEREVM"] = "hyperevm";
    // testnets
    Platform["ETHEREUM_SEPOLIA"] = "ethereum_sepolia";
    Platform["ETHEREUM_HOLESKY"] = "ethereum_holesky";
    Platform["BASE_SEPOLIA"] = "base_sepolia";
    Platform["ARBITRUM_SEPOLIA"] = "arbitrum_sepolia";
    Platform["OPTIMISM_SEPOLIA"] = "optimism_sepolia";
    Platform["POLYGON_AMOY"] = "polygon_amoy";
    Platform["BINANCE_TESTNET"] = "binance_testnet";
    Platform["OPBNB_TESTNET"] = "opbnb_testnet";
    Platform["MOONBEAM_ALPHA"] = "moonbeam_alpha";
    Platform["SONIC_BLAZE"] = "sonic_blaze";
    Platform["MANTLE_SEPOLIA"] = "mantle_sepolia";
    // Offchain platforms
    Platform["STRIPE"] = "stripe";
    Platform["CROSSMINT"] = "crossmint";
    Platform["COINBASE_ONRAMP"] = "coinbase_onramp";
    Platform["MOONPAY"] = "moonpay";
    Platform["YELLOWCARD"] = "yellowcard";
    Platform["LEKKA_CASH"] = "lekka_cash";
    Platform["OKX"] = "okx";
    Platform["COINBASE"] = "coinbase";
    Platform["KRAKEN"] = "kraken";
    Platform["GEMINI"] = "gemini";
    Platform["TURNKEY"] = "turnkey";
    Platform["MESH_CONNECT"] = "mesh_connect";
    Platform["POLYMARKET"] = "polymarket";
    Platform["RAMP_NETWORK"] = "ramp_network";
    // local testing
    Platform["HARDHAT"] = "hardhat";
    Platform["LOCAL_CHAIN_0"] = "local_chain_0";
    Platform["LOCAL_CHAIN_1"] = "local_chain_1";
})(Platform || (Platform = {}));
export var AuthType;
(function (AuthType) {
    AuthType["EMAIL"] = "email";
    AuthType["PHONE"] = "phone";
    AuthType["WALLET"] = "wallet";
})(AuthType || (AuthType = {}));
export var BalanceImpact;
(function (BalanceImpact) {
    BalanceImpact["POSITIVE"] = "positive";
    BalanceImpact["NEGATIVE"] = "negative";
    BalanceImpact["NONE"] = "none";
})(BalanceImpact || (BalanceImpact = {}));
export var PaymentMethod;
(function (PaymentMethod) {
    // Onramp methods
    PaymentMethod["APPLE_PAY"] = "apple_pay";
    PaymentMethod["GOOGLE_PAY"] = "google_pay";
    PaymentMethod["CARD"] = "card";
    PaymentMethod["ONCHAIN_TRANSFER"] = "onchain_transfer";
    // Offramp methods - US
    PaymentMethod["US_ACH"] = "us_ach";
    PaymentMethod["US_WIRE"] = "us_wire";
    // Offramp methods - Europe
    PaymentMethod["SEPA"] = "sepa";
    PaymentMethod["SEPA_INSTANT"] = "sepa_instant";
    PaymentMethod["UK_FASTER_PAYMENTS"] = "uk_faster_payments";
    // Offramp methods - Latin America
    PaymentMethod["PIX"] = "pix";
    PaymentMethod["SPEI"] = "spei";
    // Offramp methods - Africa
    PaymentMethod["MOBILE_MONEY"] = "mobile_money";
    PaymentMethod["BANK_TRANSFER_AFRICA"] = "bank_transfer_africa";
    // Offramp methods - Other
    PaymentMethod["INTERAC"] = "interac";
    PaymentMethod["SWIFT"] = "swift";
    // Offramp methods - Cash
    PaymentMethod["CASH_PICKUP"] = "cash_pickup";
})(PaymentMethod || (PaymentMethod = {}));
export var OrderSide;
(function (OrderSide) {
    OrderSide["BUY"] = "buy";
    OrderSide["SELL"] = "sell";
})(OrderSide || (OrderSide = {}));
export var InstrumentType;
(function (InstrumentType) {
    InstrumentType["FUNGIBLE_TOKEN"] = "fungible_token";
    InstrumentType["NON_FUNGIBLE_TOKEN"] = "non_fungible_token";
    InstrumentType["SEMI_FUNGIBLE_TOKEN"] = "semi_fungible_token";
    InstrumentType["FIAT"] = "fiat";
    InstrumentType["PREDICTION_MARKET"] = "prediction_market";
    InstrumentType["PERPETUAL_CONTRACT"] = "perpetual_contract";
})(InstrumentType || (InstrumentType = {}));
/**
 * Ranking metric for the polymorphic trader leaderboard. The wire values are
 * the on-the-key segment / query-param strings used by the admin API and the
 * Redis snapshot key — keep them stable. `PNL` is the default; it intentionally
 * matches the historical leaderboard ranking so the snapshot key for PnL stays
 * unchanged (see `DataCache.leaderboardSnapshotRedisKey`).
 */
export var LeaderboardMetric;
(function (LeaderboardMetric) {
    LeaderboardMetric["PNL"] = "pnl";
    LeaderboardMetric["VOLUME"] = "volume";
    LeaderboardMetric["TRADE_COUNT"] = "count";
})(LeaderboardMetric || (LeaderboardMetric = {}));
export var RampFormFieldAction;
(function (RampFormFieldAction) {
    RampFormFieldAction["OPEN_TOKEN_SELECTOR"] = "open_token_selector";
    RampFormFieldAction["OPEN_CHAIN_SELECTOR"] = "open_chain_selector";
    RampFormFieldAction["OPEN_RECIPIENT_SELECTOR"] = "open_recipient_selector";
    RampFormFieldAction["OPEN_COUNTRY_SELECTOR"] = "open_country_selector";
    RampFormFieldAction["OPEN_CURRENCY_SELECTOR"] = "open_currency_selector";
    RampFormFieldAction["OPEN_BANK_SELECTOR"] = "open_bank_selector";
})(RampFormFieldAction || (RampFormFieldAction = {}));
export var RampDirection;
(function (RampDirection) {
    RampDirection["ONRAMP"] = "onramp";
    RampDirection["OFFRAMP"] = "offramp";
})(RampDirection || (RampDirection = {}));
export var TimeIntervalUnits;
(function (TimeIntervalUnits) {
    TimeIntervalUnits["MINUTES"] = "minutes";
    TimeIntervalUnits["HOURS"] = "hours";
    TimeIntervalUnits["DAYS"] = "days";
    TimeIntervalUnits["WEEKS"] = "weeks";
    TimeIntervalUnits["MONTHS"] = "months";
    TimeIntervalUnits["YEARS"] = "years";
    TimeIntervalUnits["FOREVER"] = "forever";
})(TimeIntervalUnits || (TimeIntervalUnits = {}));
export var TokenAllowlistType;
(function (TokenAllowlistType) {
    TokenAllowlistType["ALL_TOKENS"] = "all_tokens";
    TokenAllowlistType["ONLY_FUNGIBLE_TOKENS"] = "only_fungible_tokens";
    TokenAllowlistType["ONLY_NON_FUNGIBLE_TOKENS"] = "only_non_fungible_tokens";
    TokenAllowlistType["ONLY_SEMI_FUNGIBLE_TOKENS"] = "only_semi_fungible_tokens";
})(TokenAllowlistType || (TokenAllowlistType = {}));
export var FeeCollectionUnit;
(function (FeeCollectionUnit) {
    FeeCollectionUnit["FIXED"] = "fixed";
    FeeCollectionUnit["PERCENTAGE"] = "percentage";
    FeeCollectionUnit["PERCENTAGE_WITH_CAP"] = "percentage_with_cap";
})(FeeCollectionUnit || (FeeCollectionUnit = {}));
export var FeeCollectionTarget;
(function (FeeCollectionTarget) {
    FeeCollectionTarget["NONE"] = "none";
    FeeCollectionTarget["ALL_TRANSACTIONS"] = "all_transactions";
    FeeCollectionTarget["BRIDGING_TRANSACTIONS"] = "bridging_transactions";
    FeeCollectionTarget["SWAPPING_TRANSACTIONS"] = "swapping_transactions";
    FeeCollectionTarget["GAS_TRANSACTIONS"] = "gas_transactions";
    FeeCollectionTarget["PREDICTION_BUY_TRANSACTIONS"] = "prediction_buy_transactions";
    FeeCollectionTarget["PREDICTION_SELL_TRANSACTIONS"] = "prediction_sell_transactions";
})(FeeCollectionTarget || (FeeCollectionTarget = {}));
export var FilterGroupLayout;
(function (FilterGroupLayout) {
    FilterGroupLayout["ONE_COLUMN_LIST"] = "one_column_list";
    FilterGroupLayout["TWO_COLUMN_LIST"] = "two_column_list";
    FilterGroupLayout["THREE_COLUMN_LIST"] = "three_column_list";
    FilterGroupLayout["FOUR_COLUMN_LIST"] = "four_column_list";
    FilterGroupLayout["FIVE_COLUMN_LIST"] = "five_column_list";
})(FilterGroupLayout || (FilterGroupLayout = {}));
export var QuoteType;
(function (QuoteType) {
    QuoteType["EXACT_INPUT"] = "exact_input";
    QuoteType["EXACT_OUTPUT"] = "exact_output";
    QuoteType["ONRAMP"] = "onramp";
    QuoteType["TRANSFER"] = "transfer";
})(QuoteType || (QuoteType = {}));
/**
 * Perps enums
 */
export var PerpVenue;
(function (PerpVenue) {
    PerpVenue["HYPERLIQUID"] = "hyperliquid";
})(PerpVenue || (PerpVenue = {}));
export var PositionSide;
(function (PositionSide) {
    PositionSide["LONG"] = "long";
    PositionSide["SHORT"] = "short";
})(PositionSide || (PositionSide = {}));
export var LeverageType;
(function (LeverageType) {
    LeverageType["CROSS"] = "cross";
    LeverageType["ISOLATED"] = "isolated";
})(LeverageType || (LeverageType = {}));
export var OrderStatus;
(function (OrderStatus) {
    OrderStatus["OPEN"] = "open";
    OrderStatus["PARTIALLY_FILLED"] = "partially_filled";
    OrderStatus["FILLED"] = "filled";
    OrderStatus["CANCELLED"] = "cancelled";
    OrderStatus["REJECTED"] = "rejected";
})(OrderStatus || (OrderStatus = {}));
export var OrderType;
(function (OrderType) {
    OrderType["LIMIT"] = "limit";
    OrderType["MARKET"] = "market";
    OrderType["POST_ONLY"] = "post_only";
    OrderType["IOC"] = "ioc";
    OrderType["FOK"] = "fok";
    OrderType["TWAP"] = "twap";
})(OrderType || (OrderType = {}));
export var PerpAssetType;
(function (PerpAssetType) {
    PerpAssetType["CRYPTO"] = "crypto";
    PerpAssetType["STOCK"] = "stock";
    PerpAssetType["PRE_IPO"] = "pre-ipo";
    PerpAssetType["COMMODITY"] = "commodity";
    PerpAssetType["FOREX"] = "forex";
})(PerpAssetType || (PerpAssetType = {}));
export var MarginMode;
(function (MarginMode) {
    MarginMode["CROSS"] = "cross";
    MarginMode["ISOLATED"] = "isolated";
    MarginMode["PORTFOLIO"] = "portfolio";
})(MarginMode || (MarginMode = {}));
export var PerpMarketStatus;
(function (PerpMarketStatus) {
    PerpMarketStatus["ACTIVE"] = "active";
    PerpMarketStatus["PAUSED"] = "paused";
    PerpMarketStatus["DELISTED"] = "delisted";
})(PerpMarketStatus || (PerpMarketStatus = {}));
export var TimeInForce;
(function (TimeInForce) {
    TimeInForce["GTC"] = "GTC";
    TimeInForce["IOC"] = "IOC";
    TimeInForce["FOK"] = "FOK";
    TimeInForce["ALO"] = "ALO";
})(TimeInForce || (TimeInForce = {}));
(function (OrderStatus) {
    OrderStatus["Open"] = "open";
    OrderStatus["PartiallyFilled"] = "partially_filled";
    OrderStatus["Filled"] = "filled";
    OrderStatus["Canceled"] = "canceled";
    OrderStatus["Rejected"] = "rejected";
})(OrderStatus || (OrderStatus = {}));
// ===== Positions =====
(function (PositionSide) {
    PositionSide["Long"] = "long";
    PositionSide["Short"] = "short";
})(PositionSide || (PositionSide = {}));
(function (LeverageType) {
    LeverageType["Cross"] = "cross";
    LeverageType["Isolated"] = "isolated";
})(LeverageType || (LeverageType = {}));
/**
 * How a perp fill changed the trader's position — derived from the fill
 * side/size against the position before it. Wire values match the GraphQL
 * `PerpPositionAction` enum.
 */
export var PerpPositionAction;
(function (PerpPositionAction) {
    PerpPositionAction["Open"] = "open";
    PerpPositionAction["Increase"] = "increase";
    PerpPositionAction["Reduce"] = "reduce";
    PerpPositionAction["Close"] = "close";
    PerpPositionAction["Flip"] = "flip";
})(PerpPositionAction || (PerpPositionAction = {}));
/**
 * Prediction enums
 */
/** Order type - prediction-specific order types */
export var PredictionOrderType;
(function (PredictionOrderType) {
    PredictionOrderType["LIMIT"] = "limit";
    PredictionOrderType["MARKET"] = "market";
    PredictionOrderType["GTC"] = "gtc";
    PredictionOrderType["FOK"] = "fok";
    PredictionOrderType["GTD"] = "gtd";
})(PredictionOrderType || (PredictionOrderType = {}));
export var PredictionVenue;
(function (PredictionVenue) {
    PredictionVenue["POLYMARKET"] = "polymarket";
    PredictionVenue["KALSHI"] = "kalshi";
})(PredictionVenue || (PredictionVenue = {}));
export var PredictionMarketStatus;
(function (PredictionMarketStatus) {
    PredictionMarketStatus["OPEN"] = "open";
    PredictionMarketStatus["CLOSED"] = "closed";
    PredictionMarketStatus["RESOLVED"] = "resolved";
})(PredictionMarketStatus || (PredictionMarketStatus = {}));
export var PredictionEventType;
(function (PredictionEventType) {
    PredictionEventType["SPORTS_MATCH"] = "sports_match";
    PredictionEventType["MULTI_OPTION"] = "multi_option";
    PredictionEventType["SINGLE_OPTION"] = "single_option";
})(PredictionEventType || (PredictionEventType = {}));
export var SportDisplaySectionLayoutType;
(function (SportDisplaySectionLayoutType) {
    SportDisplaySectionLayoutType["HORIZONTAL_CAROUSEL"] = "horizontal_carousel";
    SportDisplaySectionLayoutType["VERTICAL_LIST"] = "vertical_list";
})(SportDisplaySectionLayoutType || (SportDisplaySectionLayoutType = {}));
export var SportsMarketType;
(function (SportsMarketType) {
    SportsMarketType["MONEYLINE"] = "moneyline";
    SportsMarketType["SPREADS"] = "spreads";
    SportsMarketType["TOTALS"] = "totals";
    SportsMarketType["PROP"] = "prop";
})(SportsMarketType || (SportsMarketType = {}));
/** Order status - common across all venues */
export var PredictionOrderStatus;
(function (PredictionOrderStatus) {
    PredictionOrderStatus["PENDING"] = "pending";
    PredictionOrderStatus["OPEN"] = "open";
    PredictionOrderStatus["PARTIALLY_FILLED"] = "partially_filled";
    PredictionOrderStatus["FILLED"] = "filled";
    PredictionOrderStatus["CANCELLED"] = "cancelled";
    PredictionOrderStatus["EXPIRED"] = "expired";
    PredictionOrderStatus["REJECTED"] = "rejected";
})(PredictionOrderStatus || (PredictionOrderStatus = {}));
/** User trading state - based on on-chain state only (persisted) */
export var PredictionUserState;
(function (PredictionUserState) {
    PredictionUserState["WALLET_READY"] = "wallet_ready";
    PredictionUserState["DEPOSIT_CONFIRMED"] = "deposit_confirmed";
    PredictionUserState["COLLATERAL_APPROVED"] = "collateral_approved";
    PredictionUserState["TRADING_ENABLED"] = "trading_enabled";
    PredictionUserState["SELL_APPROVED"] = "sell_approved";
})(PredictionUserState || (PredictionUserState = {}));
export var PredictionEventSubcategory;
(function (PredictionEventSubcategory) {
    PredictionEventSubcategory["All"] = "all";
    PredictionEventSubcategory["ClosingSoon"] = "closing";
    // Sports subcategories
    PredictionEventSubcategory["Today"] = "today";
    PredictionEventSubcategory["Football"] = "football";
    PredictionEventSubcategory["Basketball"] = "basketball";
    PredictionEventSubcategory["Hockey"] = "hockey";
    PredictionEventSubcategory["Soccer"] = "soccer";
    PredictionEventSubcategory["Baseball"] = "baseball";
    PredictionEventSubcategory["Tennis"] = "tennis";
    PredictionEventSubcategory["Esports"] = "esports";
    PredictionEventSubcategory["Cricket"] = "cricket";
    PredictionEventSubcategory["Rugby"] = "rugby";
    PredictionEventSubcategory["Golf"] = "golf";
    PredictionEventSubcategory["Mma"] = "mma";
    PredictionEventSubcategory["Chess"] = "chess";
    PredictionEventSubcategory["Motorsports"] = "motorsports";
    // Politics subcategories
    PredictionEventSubcategory["Elections"] = "elections";
    PredictionEventSubcategory["Policy"] = "policy";
    PredictionEventSubcategory["International"] = "international";
    PredictionEventSubcategory["Domestic"] = "domestic";
    // Culture subcategories
    PredictionEventSubcategory["Entertainment"] = "entertainment";
    PredictionEventSubcategory["Movies"] = "movies";
    PredictionEventSubcategory["Music"] = "music";
    PredictionEventSubcategory["Tv"] = "tv";
    PredictionEventSubcategory["Awards"] = "awards";
    // Crypto subcategories
    PredictionEventSubcategory["Bitcoin"] = "bitcoin";
    PredictionEventSubcategory["Ethereum"] = "ethereum";
    PredictionEventSubcategory["Defi"] = "defi";
    PredictionEventSubcategory["Nft"] = "nft";
    PredictionEventSubcategory["Regulation"] = "regulation";
    // Finance subcategories
    PredictionEventSubcategory["Stocks"] = "stocks";
    PredictionEventSubcategory["Forex"] = "forex";
    PredictionEventSubcategory["Commodities"] = "commodities";
    PredictionEventSubcategory["Bonds"] = "bonds";
    PredictionEventSubcategory["Economy"] = "economy";
    // World subcategories
    PredictionEventSubcategory["Geopolitics"] = "geopolitics";
    PredictionEventSubcategory["Conflicts"] = "conflicts";
    PredictionEventSubcategory["Trade"] = "trade";
    PredictionEventSubcategory["Diplomacy"] = "diplomacy";
    // Tech subcategories
    PredictionEventSubcategory["Ai"] = "ai";
    PredictionEventSubcategory["Space"] = "space";
    PredictionEventSubcategory["Biotech"] = "biotech";
    PredictionEventSubcategory["Energy"] = "energy";
    PredictionEventSubcategory["Innovation"] = "innovation";
    // Climate subcategories
    PredictionEventSubcategory["Temperature"] = "temperature";
    PredictionEventSubcategory["Weather"] = "weather";
    PredictionEventSubcategory["Renewable"] = "renewable";
})(PredictionEventSubcategory || (PredictionEventSubcategory = {}));
export var DeviceType;
(function (DeviceType) {
    DeviceType["IOS"] = "ios";
    DeviceType["ANDROID"] = "android";
    DeviceType["WINDOWS"] = "windows";
    DeviceType["MACOS"] = "macos";
    DeviceType["WEB"] = "web";
    DeviceType["UNKNOWN"] = "unknown";
})(DeviceType || (DeviceType = {}));
